import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Pencil, Trash2, Plus, RotateCcw, AlertTriangle,
  Download, Undo2, Mail, RefreshCw,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import type { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { AdminShell } from "@/components/admin/AdminShell";

type BookingRow = Database["public"]["Tables"]["bookings"]["Row"];
type BlogPostRow = Database["public"]["Tables"]["blog_posts"]["Row"];
type ProductRow = Database["public"]["Tables"]["products"]["Row"];
type PostStatus = Database["public"]["Enums"]["post_status"];
type BookingStatus = Database["public"]["Enums"]["booking_status"];
type PaymentStatus = Database["public"]["Enums"]["payment_status"];

export const AdminLayout = ({ children, title }: { children: React.ReactNode; title: string }) => (
  <AdminShell title={title}>{children}</AdminShell>
);

const StatCard = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className="bg-panel border border-border p-6">
    <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-2">{label}</p>
    <p className="text-3xl font-display text-primary">{value}</p>
    {sub && <p className="text-xs text-muted-foreground font-body mt-1">{sub}</p>}
  </div>
);

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

// ============ DASHBOARD ============
const AdminDashboard = () => {
  const [stats, setStats] = useState({ bookings: 0, revenue: 0, customers: 0, posts: 0 });
  const [recent, setRecent] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [stripeInfo, setStripeInfo] = useState<{
    mode: string; account_id?: string; business_name?: string | null;
    charges_enabled?: boolean; payouts_enabled?: boolean; error?: string;
  } | null>(null);

  useEffect(() => {
    (async () => {
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      const [bk, rev, cust, posts, recentBk] = await Promise.all([
        supabase.from("bookings").select("id", { count: "exact", head: true }),
        supabase.from("bookings").select("amount_paid").gte("created_at", monthStart.toISOString()),
        supabase.from("customers").select("id", { count: "exact", head: true }),
        supabase.from("blog_posts").select("id", { count: "exact", head: true }).eq("status", "published"),
        supabase.from("bookings").select("*").order("created_at", { ascending: false }).limit(5),
      ]);

      const revenue = (rev.data ?? []).reduce((s, r) => s + Number(r.amount_paid || 0), 0);
      setStats({
        bookings: bk.count ?? 0,
        revenue,
        customers: cust.count ?? 0,
        posts: posts.count ?? 0,
      });
      setRecent(recentBk.data ?? []);
      setLoading(false);
    })();
    (async () => {
      const { data, error } = await supabase.functions.invoke("stripe-account-info");
      if (error) { setStripeInfo({ mode: "unknown", error: error.message }); return; }
      setStripeInfo(data);
    })();
  }, []);

  return (
    <AdminLayout title="DASHBOARD">
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <>
          {stripeInfo && (
            <div className="bg-panel border border-border p-4 mb-6 flex flex-wrap items-center gap-4">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Stripe</span>
              {stripeInfo.mode === "live" ? (
                <span className="px-3 py-1 text-xs font-mono uppercase border border-emerald-500/40 bg-emerald-500/15 text-emerald-400">LIVE MODE</span>
              ) : stripeInfo.mode === "test" ? (
                <span className="px-3 py-1 text-xs font-mono uppercase border border-yellow-500/40 bg-yellow-500/15 text-yellow-400">TEST MODE</span>
              ) : (
                <span className="px-3 py-1 text-xs font-mono uppercase border border-border text-muted-foreground">UNKNOWN</span>
              )}
              {stripeInfo.business_name && (
                <span className="text-sm font-body">{stripeInfo.business_name}</span>
              )}
              {stripeInfo.account_id && (
                <span className="text-xs font-mono text-muted-foreground">{stripeInfo.account_id}</span>
              )}
              {stripeInfo.charges_enabled === false && (
                <span className="flex items-center gap-1 text-xs text-red-400 font-mono">
                  <AlertTriangle className="w-3 h-3" /> Charges disabled
                </span>
              )}
              {stripeInfo.error && (
                <span className="text-xs text-red-400 font-mono">{stripeInfo.error}</span>
              )}
              <div className="ml-auto flex gap-2">
                <Button asChild variant="outline" size="sm"><Link to="/admin/stripe/sync">Sync</Link></Button>
                <Button asChild variant="outline" size="sm"><Link to="/admin/stripe/events">Events</Link></Button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <StatCard label="Total Bookings" value={String(stats.bookings)} />
            <StatCard label="Revenue (MTD)" value={fmtMoney(stats.revenue)} />
            <StatCard label="Customers" value={String(stats.customers)} />
            <StatCard label="Published Posts" value={String(stats.posts)} />
          </div>
          <div className="bg-panel border border-border p-6">
            <h2 className="font-display tracking-widest mb-4">RECENT BOOKINGS</h2>
            {recent.length === 0 ? (
              <p className="text-muted-foreground font-body text-sm">No bookings yet.</p>
            ) : (
              <div className="space-y-2">
                {recent.map((b) => (
                  <div key={b.id} className="flex items-center justify-between border-b border-border pb-2 last:border-0">
                    <div>
                      <p className="font-body text-sm">{new Date(b.start_at).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground font-mono uppercase">{b.status} · {b.payment_status}</p>
                    </div>
                    <p className="font-display text-primary">{fmtMoney(Number(b.total))}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </AdminLayout>
  );
};

// ============ BOOKINGS CRM ============
const AdminBookings = () => {
  const [rows, setRows] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [paymentFilter, setPaymentFilter] = useState<string>("all");
  const [invoiceFilter, setInvoiceFilter] = useState<string>("all"); // all | sent | not_sent
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [invoiceSentSet, setInvoiceSentSet] = useState<Set<string>>(new Set());
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const { user } = useAuth();

  // ---- Manual create dialog ----
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [studios, setStudios] = useState<{ id: string; name: string; hourly_rate: number }[]>([]);
  const [draft, setDraft] = useState({
    studio_id: "",
    date: "",
    start_time: "10:00",
    hours: "2",
    customer_name: "",
    customer_email: "",
    total: "",
    status: "confirmed" as BookingStatus,
    payment_status: "paid" as PaymentStatus,
    notes: "",
    internal_notes: "",
  });

  useEffect(() => {
    supabase
      .from("studios")
      .select("id,name,hourly_rate")
      .eq("active", true)
      .order("display_order")
      .then(({ data }) => setStudios(data ?? []));
  }, []);

  const resetDraft = () =>
    setDraft({
      studio_id: "", date: "", start_time: "10:00", hours: "2",
      customer_name: "", customer_email: "", total: "",
      status: "confirmed", payment_status: "paid",
      notes: "", internal_notes: "",
    });

  const createBooking = async () => {
    if (!draft.studio_id) return toast.error("Pick a studio");
    if (!draft.date) return toast.error("Pick a date");
    const hours = Number(draft.hours);
    if (!hours || hours <= 0) return toast.error("Hours must be greater than 0");
    const start = new Date(`${draft.date}T${draft.start_time || "00:00"}`);
    if (isNaN(start.getTime())) return toast.error("Invalid date/time");
    const end = new Date(start.getTime() + hours * 3600_000);
    const startISO = start.toISOString();
    const endISO = end.toISOString();

    setCreating(true);
    try {
      // Resolve customer (optional)
      let customer_id: string | null = null;
      const email = draft.customer_email.trim().toLowerCase();
      if (email) {
        const { data: existing } = await supabase
          .from("customers").select("id").eq("email", email).maybeSingle();
        if (existing) {
          customer_id = existing.id;
        } else {
          const { data: ins, error: insErr } = await supabase
            .from("customers")
            .insert({ email, full_name: draft.customer_name.trim() || null })
            .select("id").single();
          if (insErr) throw insErr;
          customer_id = ins.id;
        }
      }

      const studio = studios.find((s) => s.id === draft.studio_id);
      const hours = (new Date(endISO).getTime() - new Date(startISO).getTime()) / 3600_000;
      const computed = studio ? Number((studio.hourly_rate * hours).toFixed(2)) : 0;
      const total = draft.total ? Number(draft.total) : computed;
      const amount_paid = draft.payment_status === "paid" ? total
        : draft.payment_status === "partial" ? total / 2 : 0;

      const { error } = await supabase.from("bookings").insert({
        studio_id: draft.studio_id,
        start_at: startISO,
        end_at: endISO,
        customer_id,
        status: draft.status,
        payment_status: draft.payment_status,
        subtotal: total,
        total,
        amount_paid,
        notes: draft.notes || null,
        internal_notes: draft.internal_notes || null,
      });
      if (error) throw error;
      toast.success("Booking created");
      setCreateOpen(false);
      resetDraft();
      load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to create booking");
    } finally {
      setCreating(false);
    }
  };

  const load = async () => {
    setLoading(true);
    let q = supabase.from("bookings").select("*").order("start_at", { ascending: false });
    if (statusFilter !== "all") q = q.eq("status", statusFilter as BookingStatus);
    if (paymentFilter !== "all") q = q.eq("payment_status", paymentFilter as PaymentStatus);
    if (dateFrom) q = q.gte("start_at", new Date(dateFrom).toISOString());
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      q = q.lte("start_at", to.toISOString());
    }
    const { data, error } = await q;
    if (error) toast.error(error.message);
    const list = (data ?? []) as BookingRow[];
    // Invoice-sent set (used both for the UI badge & invoice filter)
    const ids = list.map((r) => r.id);
    let sent = new Set<string>();
    if (ids.length) {
      const { data: el } = await supabase
        .from("email_send_log")
        .select("status,metadata,template_name")
        .in("template_name", ["booking-invoice-link"]);
      (el ?? []).forEach((e: any) => {
        const bid = e?.metadata?.booking_id;
        if (bid && ids.includes(bid) && e.status === "sent") sent.add(bid);
      });
    }
    setInvoiceSentSet(sent);
    const filtered = invoiceFilter === "all"
      ? list
      : list.filter((b) => (invoiceFilter === "sent" ? sent.has(b.id) : !sent.has(b.id)));
    setRows(filtered);
    setLoading(false);
  };

  useEffect(() => { load(); }, [statusFilter, paymentFilter, invoiceFilter, dateFrom, dateTo]);

  const updateBooking = async (id: string, patch: Partial<BookingRow>) => {
    const { error } = await supabase.from("bookings").update(patch).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Booking updated");
    load();
  };

  const sendPreviewToMe = async (b: BookingRow) => {
    if (!user?.email) return toast.error("No admin email on file");
    setSendingId(b.id);
    try {
      // Fetch studio + customer for accurate template data
      const [{ data: studio }, { data: customer }] = await Promise.all([
        supabase.from("studios").select("name").eq("id", b.studio_id).maybeSingle(),
        b.customer_id
          ? supabase.from("customers").select("full_name, email").eq("id", b.customer_id).maybeSingle()
          : Promise.resolve({ data: null as { full_name: string | null; email: string } | null }),
      ]);
      const fmt = (iso: string) =>
        new Date(iso).toLocaleString("en-US", {
          weekday: "long", year: "numeric", month: "long", day: "numeric",
          hour: "numeric", minute: "2-digit", timeZoneName: "short",
        });
      const { error } = await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "booking-confirmation",
          recipientEmail: user.email,
          // Unique key per admin+booking so repeated previews aren't blocked
          idempotencyKey: `booking-confirm-preview-${b.id}-${Date.now()}`,
          templateData: {
            customerName: customer?.full_name || user.email,
            studioName: studio?.name || "Studio",
            startAt: fmt(b.start_at),
            endAt: fmt(b.end_at),
            totalPaid: Number(b.total).toFixed(2),
            bookingId: b.id,
          },
        },
      });
      if (error) throw error;
      toast.success(`Preview sent to ${user.email}`);
    } catch (e: any) {
      toast.error(e?.message || "Failed to send preview");
    } finally {
      setSendingId(null);
    }
  };

  const resendToCustomer = async (b: BookingRow) => {
    if (!confirm("Re-send the booking confirmation email to the customer for this booking?")) return;
    setResendingId(b.id);
    try {
      const { data, error } = await supabase.functions.invoke(
        "resend-booking-confirmation",
        { body: { booking_id: b.id } },
      );
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(`Resent to ${(data as any)?.recipient ?? "customer"}`);
    } catch (e: any) {
      toast.error(e?.message || "Failed to resend");
    } finally {
      setResendingId(null);
    }
  };

  const csvEscape = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const ids = rows.map((r) => r.id);
      const [{ data: addons }, { data: studiosAll }, { data: customersAll }, { data: emails }] = await Promise.all([
        ids.length
          ? supabase.from("booking_addons").select("booking_id,quantity,unit_price,option_label,addons(name)").in("booking_id", ids)
          : Promise.resolve({ data: [] as any[] }),
        supabase.from("studios").select("id,name"),
        supabase.from("customers").select("id,full_name,email,phone"),
        ids.length
          ? supabase.from("email_send_log")
              .select("template_name,status,metadata")
              .in("template_name", ["booking-confirmation", "booking-invoice-link"])
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const studioMap = new Map((studiosAll ?? []).map((s: any) => [s.id, s.name]));
      const custMap = new Map((customersAll ?? []).map((c: any) => [c.id, c]));
      const addonsByBooking = new Map<string, any[]>();
      (addons ?? []).forEach((a: any) => {
        const arr = addonsByBooking.get(a.booking_id) ?? [];
        arr.push(a);
        addonsByBooking.set(a.booking_id, arr);
      });
      const invoiceSentByBooking = new Set<string>();
      (emails ?? []).forEach((e: any) => {
        const bid = e?.metadata?.booking_id;
        if (bid && e.template_name === "booking-invoice-link" && e.status === "sent") {
          invoiceSentByBooking.add(bid);
        }
      });

      const header = [
        "booking_id", "status", "payment_status", "studio", "start_at", "end_at",
        "customer_name", "customer_email", "customer_phone",
        "subtotal", "total", "amount_paid",
        "stripe_payment_intent", "stripe_session_id",
        "addons", "addons_total", "invoice_emailed",
        "created_at", "notes",
      ];
      const lines = [header.join(",")];
      for (const b of rows) {
        const cust: any = b.customer_id ? custMap.get(b.customer_id) : null;
        const addonRows = addonsByBooking.get(b.id) ?? [];
        const addonsStr = addonRows.map((a) => {
          const name = a.addons?.name ?? "addon";
          const label = a.option_label ? `${name} (${a.option_label})` : name;
          return `${a.quantity}× ${label} @ $${Number(a.unit_price).toFixed(2)}`;
        }).join("; ");
        const addonsTotal = addonRows.reduce((s, a) => s + Number(a.unit_price) * Number(a.quantity || 1), 0);
        lines.push([
          b.id, b.status, b.payment_status, studioMap.get(b.studio_id) ?? b.studio_id,
          b.start_at, b.end_at,
          cust?.full_name ?? "", cust?.email ?? "", cust?.phone ?? "",
          Number(b.subtotal).toFixed(2), Number(b.total).toFixed(2), Number(b.amount_paid).toFixed(2),
          b.stripe_payment_intent ?? "", (b as any).stripe_checkout_session_id ?? "",
          addonsStr, addonsTotal.toFixed(2),
          invoiceSentByBooking.has(b.id) ? "yes" : "no",
          b.created_at, b.notes ?? "",
        ].map(csvEscape).join(","));
      }
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `bookings-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${rows.length} bookings`);
    } catch (e: any) {
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout title="BOOKINGS">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-muted-foreground font-body text-sm">{rows.length} bookings</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 font-body" aria-label="From date" />
          <span className="text-muted-foreground text-xs">→</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 font-body" aria-label="To date" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 font-body"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
          <Select value={paymentFilter} onValueChange={setPaymentFilter}>
            <SelectTrigger className="w-44 font-body"><SelectValue placeholder="Payment" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All payments</SelectItem>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
          <Select value={invoiceFilter} onValueChange={setInvoiceFilter}>
            <SelectTrigger className="w-44 font-body"><SelectValue placeholder="Invoice" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any invoice status</SelectItem>
              <SelectItem value="sent">Invoice emailed</SelectItem>
              <SelectItem value="not_sent">Invoice NOT emailed</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportCsv} variant="outline" disabled={exporting || rows.length === 0} className="font-body">
            {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            Export CSV
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="font-body">
            <Plus className="w-4 h-4 mr-1" /> New booking
          </Button>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) resetDraft(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New booking</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Studio</Label>
              <Select value={draft.studio_id} onValueChange={(v) => setDraft({ ...draft, studio_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select studio" /></SelectTrigger>
                <SelectContent>
                  {studios.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} — ${s.hourly_rate}/hr</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-3">
                <Label>Date</Label>
                <DateTimePicker
                  mode="date"
                  value={draft.date}
                  onChange={(v) => setDraft({ ...draft, date: v })}
                />
              </div>
              <div>
                <Label>Start time</Label>
                <Input
                  type="time"
                  value={draft.start_time}
                  onChange={(e) => setDraft({ ...draft, start_time: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Hours</Label>
                <Select
                  value={draft.hours}
                  onValueChange={(v) => setDraft({ ...draft, hours: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select hours" /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12].map((h) => (
                      <SelectItem key={h} value={String(h)}>{h} {h === 1 ? "hour" : "hours"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Customer name</Label>
                <Input value={draft.customer_name}
                  onChange={(e) => setDraft({ ...draft, customer_name: e.target.value })} />
              </div>
              <div>
                <Label>Customer email</Label>
                <Input type="email" value={draft.customer_email}
                  onChange={(e) => setDraft({ ...draft, customer_email: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Total ($)</Label>
                <Input type="number" step="0.01" placeholder="auto"
                  value={draft.total}
                  onChange={(e) => setDraft({ ...draft, total: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v as BookingStatus })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment</Label>
                <Select value={draft.payment_status} onValueChange={(v) => setDraft({ ...draft, payment_status: v as PaymentStatus })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes (visible)</Label>
              <Textarea rows={2} value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            </div>
            <div>
              <Label>Internal notes</Label>
              <Textarea rows={2} value={draft.internal_notes}
                onChange={(e) => setDraft({ ...draft, internal_notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createBooking} disabled={creating}>
              {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Create booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-panel border border-border p-6">
          <p className="text-muted-foreground font-body text-sm">No bookings match.</p>
        </div>
      ) : (
        <div className="bg-panel border border-border overflow-hidden">
          {rows.map((b, i) => (
            <div key={b.id} className={`p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3 md:items-center ${i < rows.length - 1 ? "border-b border-border" : ""}`}>
              <div>
                <p className="font-body text-sm">{new Date(b.start_at).toLocaleString()} → {new Date(b.end_at).toLocaleTimeString()}</p>
                <p className="text-xs text-muted-foreground font-mono">{b.notes || "—"}</p>
                <Link to={`/admin/bookings/${b.id}`} className="text-xs text-primary font-mono hover:underline">View detail →</Link>
              </div>
              <p className="font-display text-primary">{fmtMoney(Number(b.total))}</p>
              <Select value={b.status} onValueChange={(v) => updateBooking(b.id, { status: v as BookingStatus })}>
                <SelectTrigger className="w-36 h-8 text-xs font-mono uppercase"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
              <Select value={b.payment_status} onValueChange={(v) => updateBooking(b.id, { payment_status: v as PaymentStatus })}>
                <SelectTrigger className="w-32 h-8 text-xs font-mono uppercase"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
              {b.status !== "cancelled" && b.status !== "refunded" && (
                <Button variant="outline" size="sm" onClick={async () => {
                  const hours = Math.max(0, Math.floor((new Date(b.start_at).getTime() - Date.now()) / 36e5));
                  const reason = prompt(`Cancel this booking?\n\nHours until start: ${hours}\nAmount paid: ${fmtMoney(Number(b.amount_paid))}\n\nThe configured refund policy will be applied automatically. Optional reason for the customer:`, "");
                  if (reason === null) return;
                  const { data, error } = await supabase.functions.invoke("cancel-booking", {
                    body: { booking_id: b.id, reason, notify_customer: true },
                  });
                  if (error || (data as any)?.error) return toast.error(error?.message || (data as any)?.error);
                  toast.success(`Cancelled — refunded ${fmtMoney(Number((data as any)?.refund_amount ?? 0))} (policy: ${(data as any)?.policy ?? "none"})`);
                  load();
                }} className="font-body text-xs text-destructive hover:text-destructive">
                  <Undo2 className="w-3 h-3 mr-1" /> Cancel
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={sendingId === b.id}
                onClick={() => sendPreviewToMe(b)}
                className="font-body text-xs"
                title="Send a booking-confirmation preview to your admin email"
              >
                {sendingId === b.id ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Mail className="w-3 h-3 mr-1" />
                )}
                Email me
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={resendingId === b.id}
                onClick={() => resendToCustomer(b)}
                className="font-body text-xs"
                title="Re-send booking confirmation to the customer on file"
              >
                {resendingId === b.id ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <RotateCcw className="w-3 h-3 mr-1" />
                )}
                Resend
              </Button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

// ============ BLOG EDITOR ============
const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

type PostDraft = {
  id?: string;
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  cover_image: string;
  status: PostStatus;
};

const emptyPost: PostDraft = { title: "", slug: "", excerpt: "", body: "", cover_image: "", status: "draft" };

const AdminBlog = () => {
  const [posts, setPosts] = useState<BlogPostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PostDraft>(emptyPost);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("blog_posts").select("*").order("updated_at", { ascending: false });
    if (error) toast.error(error.message);
    setPosts(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft(emptyPost); setOpen(true); };
  const openEdit = (p: BlogPostRow) => {
    setDraft({
      id: p.id, title: p.title, slug: p.slug,
      excerpt: p.excerpt ?? "", body: p.body ?? "",
      cover_image: p.cover_image ?? "", status: p.status,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!draft.title.trim()) return toast.error("Title required");
    const slug = draft.slug.trim() || slugify(draft.title);
    setSaving(true);
    const payload = {
      title: draft.title.trim(),
      slug,
      excerpt: draft.excerpt || null,
      body: draft.body || null,
      cover_image: draft.cover_image || null,
      status: draft.status,
      publish_at: draft.status === "published" ? new Date().toISOString() : null,
    };
    const { error } = draft.id
      ? await supabase.from("blog_posts").update(payload).eq("id", draft.id)
      : await supabase.from("blog_posts").insert(payload);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(draft.id ? "Post updated" : "Post created");
    setOpen(false);
    load();
  };

  const del = async (id: string) => {
    if (!confirm("Delete this post?")) return;
    const { error } = await supabase.from("blog_posts").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Post deleted");
    load();
  };

  return (
    <AdminLayout title="BLOG POSTS">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground font-body text-sm">{posts.length} posts</p>
        <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-6">
          <Plus className="w-4 h-4 mr-1" /> New Post
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : posts.length === 0 ? (
        <div className="bg-panel border border-border p-6">
          <p className="text-muted-foreground font-body text-sm">No posts yet. Click “New Post” to get started.</p>
        </div>
      ) : (
        <div className="bg-panel border border-border overflow-hidden">
          {posts.map((p, i) => (
            <div key={p.id} className={`p-4 flex items-center justify-between gap-3 ${i < posts.length - 1 ? "border-b border-border" : ""}`}>
              <div className="min-w-0">
                <h3 className="font-body font-medium text-sm truncate">{p.title}</h3>
                <p className="text-xs text-muted-foreground font-mono mt-1 uppercase">/{p.slug} · {p.status}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => openEdit(p)} className="font-body text-xs">
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => del(p.id)} className="font-body text-xs text-destructive hover:text-destructive">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-widest">{draft.id ? "EDIT POST" : "NEW POST"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Title</Label>
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value, slug: draft.slug || slugify(e.target.value) })} />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Slug</Label>
              <Input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })} />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Cover Image URL</Label>
              <Input value={draft.cover_image} onChange={(e) => setDraft({ ...draft, cover_image: e.target.value })} placeholder="https://…" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Excerpt</Label>
              <Textarea rows={2} value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Body</Label>
              <RichTextEditor value={draft.body} onChange={(html) => setDraft({ ...draft, body: html })} />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Status</Label>
              <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v as PostStatus })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {draft.id ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// ============ PRODUCTS CRM ============
type ProductDraft = {
  id?: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  compare_at_price: number | null;
  category: string;
  active: boolean;
  image_url: string;
};
const emptyProduct: ProductDraft = {
  name: "", slug: "", description: "", price: 0,
  compare_at_price: null, category: "", active: true, image_url: "",
};

const AdminProducts = () => {
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ProductDraft>(emptyProduct);
  const [saving, setSaving] = useState(false);
  // Per-product add-ons (attributes)
  type AddonRow = Database["public"]["Tables"]["addons"]["Row"];
  const [pAddons, setPAddons] = useState<AddonRow[]>([]);
  const [pAddonsLoading, setPAddonsLoading] = useState(false);
  const [newAddon, setNewAddon] = useState<{ name: string; price: string; max_quantity: string }>({ name: "", price: "", max_quantity: "" });

  const loadProductAddons = async (productId: string) => {
    setPAddonsLoading(true);
    const { data, error } = await supabase
      .from("addons")
      .select("*")
      .eq("product_id", productId)
      .order("display_order")
      .order("created_at");
    if (error) toast.error(error.message);
    setPAddons(data ?? []);
    setPAddonsLoading(false);
  };

  const addProductAddon = async () => {
    if (!draft.id) return toast.error("Save the product first");
    const name = newAddon.name.trim();
    const price = Number(newAddon.price);
    if (!name) return toast.error("Add-on name required");
    if (!Number.isFinite(price) || price < 0) return toast.error("Valid price required");
    const maxQ = newAddon.max_quantity ? Math.max(1, parseInt(newAddon.max_quantity, 10)) : null;
    const { error } = await supabase.from("addons").insert({
      name, price, product_id: draft.id, max_quantity: maxQ, pricing_unit: "flat", active: true,
      display_order: pAddons.length,
    });
    if (error) return toast.error(error.message);
    setNewAddon({ name: "", price: "", max_quantity: "" });
    loadProductAddons(draft.id);
  };

  const toggleProductAddon = async (a: AddonRow) => {
    const { error } = await supabase.from("addons").update({ active: !a.active }).eq("id", a.id);
    if (error) return toast.error(error.message);
    if (draft.id) loadProductAddons(draft.id);
  };

  const deleteProductAddon = async (id: string) => {
    if (!confirm("Delete this add-on?")) return;
    const { error } = await supabase.from("addons").delete().eq("id", id);
    if (error) return toast.error(error.message);
    if (draft.id) loadProductAddons(draft.id);
  };

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("products").select("*").order("display_order").order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft(emptyProduct); setOpen(true); };
  const openEdit = (p: ProductRow) => {
    const imgs = Array.isArray(p.images) ? (p.images as unknown[]) : [];
    setDraft({
      id: p.id, name: p.name, slug: p.slug,
      description: p.description ?? "", price: Number(p.price),
      compare_at_price: p.compare_at_price ? Number(p.compare_at_price) : null,
      category: p.category ?? "", active: p.active,
      image_url: typeof imgs[0] === "string" ? (imgs[0] as string) : "",
    });
    loadProductAddons(p.id);
    setOpen(true);
  };

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Name required");
    const slug = draft.slug.trim() || slugify(draft.name);
    setSaving(true);
    const payload = {
      name: draft.name.trim(),
      slug,
      description: draft.description || null,
      price: draft.price,
      compare_at_price: draft.compare_at_price,
      category: draft.category || null,
      active: draft.active,
      images: draft.image_url ? [draft.image_url] : [],
    };
    const { error } = draft.id
      ? await supabase.from("products").update(payload).eq("id", draft.id)
      : await supabase.from("products").insert(payload);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(draft.id ? "Product updated" : "Product created");
    setOpen(false);
    load();
  };

  const del = async (id: string) => {
    if (!confirm("Delete this product?")) return;
    const { error } = await supabase.from("products").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Product deleted");
    load();
  };

  return (
    <AdminLayout title="PRODUCTS">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground font-body text-sm">{rows.length} products</p>
        <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-6">
          <Plus className="w-4 h-4 mr-1" /> Add Product
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-panel border border-border p-6">
          <p className="text-muted-foreground font-body text-sm">No products yet.</p>
        </div>
      ) : (
        <div className="bg-panel border border-border overflow-hidden">
          {rows.map((p, i) => (
            <div key={p.id} className={`p-4 flex items-center justify-between gap-3 ${i < rows.length - 1 ? "border-b border-border" : ""}`}>
              <div className="min-w-0">
                <h3 className="font-body font-medium text-sm truncate">{p.name}</h3>
                <p className="text-xs text-primary font-mono mt-1">{fmtMoney(Number(p.price))} · {p.active ? "Active" : "Hidden"}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={async () => {
                  toast.loading("Syncing to Stripe…", { id: `sync-${p.id}` });
                  const { data, error } = await supabase.functions.invoke("stripe-sync-product", { body: { product_id: p.id } });
                  if (error || data?.error) return toast.error(error?.message || data?.error, { id: `sync-${p.id}` });
                  toast.success(p.stripe_product_id ? "Stripe updated" : "Synced to Stripe", { id: `sync-${p.id}` });
                  load();
                }} className="font-body text-xs">
                  <RefreshCw className="w-3 h-3 mr-1" /> {p.stripe_product_id ? "Resync" : "Sync"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(p)} className="font-body text-xs">
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => del(p.id)} className="font-body text-xs text-destructive hover:text-destructive">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-widest">{draft.id ? "EDIT PRODUCT" : "NEW PRODUCT"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value, slug: draft.slug || slugify(e.target.value) })} />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Slug</Label>
              <Input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Price (USD)</Label>
                <Input type="number" step="0.01" value={draft.price} onChange={(e) => setDraft({ ...draft, price: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Compare-at</Label>
                <Input type="number" step="0.01" value={draft.compare_at_price ?? ""} onChange={(e) => setDraft({ ...draft, compare_at_price: e.target.value === "" ? null : Number(e.target.value) })} />
              </div>
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Category</Label>
              <Input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="apparel, accessories, …" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Image URL</Label>
              <Input value={draft.image_url} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })} placeholder="https://…" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Description</Label>
              <Textarea rows={4} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: v })} />
              <Label className="font-mono text-xs uppercase tracking-widest">Active (visible on shop)</Label>
            </div>

            {draft.id && (
              <div className="pt-4 border-t border-border">
                <Label className="font-mono text-xs uppercase tracking-widest mb-2 block">Attributes / Add-ons</Label>
                <p className="text-xs text-muted-foreground mb-3">Optional extras customers can add when booking this product (e.g. Backdrop color +$10, Throne chair +$65).</p>
                {pAddonsLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
                ) : (
                  <div className="space-y-2 mb-3">
                    {pAddons.length === 0 && (
                      <p className="text-xs text-muted-foreground">No add-ons yet.</p>
                    )}
                    {pAddons.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 border border-border p-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-body truncate">{a.name}</p>
                          <p className="text-xs text-primary font-mono">{fmtMoney(Number(a.price))}{a.max_quantity ? ` · max ${a.max_quantity}` : ""}{!a.active ? " · hidden" : ""}</p>
                        </div>
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => toggleProductAddon(a)}>
                          {a.active ? "Hide" : "Show"}
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs text-destructive hover:text-destructive" onClick={() => deleteProductAddon(a.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-[1fr_110px_100px_auto] gap-2 items-end">
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-widest">Name</Label>
                    <Input value={newAddon.name} onChange={(e) => setNewAddon({ ...newAddon, name: e.target.value })} placeholder="Throne chair" />
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-widest">Price</Label>
                    <Input type="number" step="0.01" value={newAddon.price} onChange={(e) => setNewAddon({ ...newAddon, price: e.target.value })} placeholder="65" />
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-widest">Max qty</Label>
                    <Input type="number" min="1" value={newAddon.max_quantity} onChange={(e) => setNewAddon({ ...newAddon, max_quantity: e.target.value })} placeholder="—" />
                  </div>
                  <Button size="sm" onClick={addProductAddon} className="bg-primary text-primary-foreground hover:bg-primary/90">
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {draft.id ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// ============ Phase C CRMs live in AdminExtras.tsx ============
export {
  AdminGallery, AdminTeam, AdminTestimonials,
  AdminSubscribers, AdminContact, AdminSettings,
} from "./AdminExtras";

export { AdminDashboard, AdminBookings, AdminBlog, AdminProducts };
